import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    inject,
    OnInit,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { Button, Popover, Tabs } from '@ntv360/component-pantry';
import { FilterOption, TableToolbarButton } from '@shared';
import { ConfirmationModalComponent } from '@shared/components/modals/confirmation-modal/confirmation-modal.component';
import { SVG_ICONS } from '@shared/constants';
import { Logger, withLoading } from '@shared/utils';

import { AuthService } from '@core/services/auth/auth.service';
import { LicensesService } from '@core/services/licenses/licenses.service';
import { SocketService } from '@core/services/socket/socket.service';
import { MainLayoutStore } from '@layouts/main-layout';
import { SubNavbarService } from '@layouts/main-layout/components';
import {
    SINGLE_LICENSE_TABLE_COLUMNS,
    SINGLE_LICENSE_TABLE_DATA,
} from '../../../dealers/constants/dummyData.constants';
import { DetailsConceptComponent } from './tabs/details-concept/details-concept.component';

@Component({
    selector: 'app-single-license',
    standalone: true,
    imports: [Tabs, Button, Popover, DetailsConceptComponent, ConfirmationModalComponent],
    templateUrl: './single-license.component.html',
    styleUrl: './single-license.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SingleLicenseComponent implements OnInit {
    /**
     * Reference to inject service to modify sub navbar component
     */

    private subNavbarService = inject(SubNavbarService);
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private licensesService = inject(LicensesService);
    private socketService = inject(SocketService);
    private destroyRef = inject(DestroyRef);
    private authService = inject(AuthService);
    /**
     * Reference to navbar icon constants.
     */
    public icons = SVG_ICONS;

    /**
     * Path to the SVG sprite file.
     */
    public spritePath = 'assets/icons/icons-sprite.svg';

    /**
     * Items to be displayed in the actions popover
     */
    public actionItems = [
        { label: 'Reboot Devices', icon: this.icons.arrowRotate },
        { label: 'Push Updates', icon: this.icons.arrowRotate },
    ];

    /**
     * Type of pending license action awaiting user confirmation.
     * `null` means no pending action.
     */
    public pendingAction = signal<'reboot' | 'push-updates' | null>(null);

    /**
     * Tracks the in-flight action request for the modal spinner.
     */
    public isActionRunning = signal<boolean>(false);

    /**
     * Human-readable label for the current license (alias → key → id).
     * Used inside the action confirmation and success modal captions.
     */
    public licenseDisplayName = signal<string>('');

    /**
     * Last completed action shown in the success modal after confirm.
     * `null` means no success modal is open.
     */
    public completedAction = signal<'reboot' | 'push-updates' | null>(null);

    /**
     * Title shown in the action confirmation modal.
     */
    public actionModalTitle = computed<string>(() => {
        switch (this.pendingAction()) {
            case 'reboot':
                return 'Reboot Devices';
            case 'push-updates':
                return 'Push Updates';
            default:
                return '';
        }
    });

    /**
     * Message shown in the action confirmation modal.
     * Substitutes the license alias/key so the user sees which license is targeted.
     */
    public actionModalMessage = computed<string>(() => {
        const name = this.licenseDisplayName() || 'this license';
        switch (this.pendingAction()) {
            case 'reboot':
                return `This will reboot all devices linked to "${name}". Active playback will be interrupted briefly. Do you want to continue?`;
            case 'push-updates':
                return `This will push the latest updates to "${name}". Devices may restart during the update. Do you want to continue?`;
            default:
                return '';
        }
    });

    /**
     * Title shown in the success modal after a license action completes.
     */
    public actionSuccessTitle = computed<string>(() => {
        switch (this.completedAction()) {
            case 'reboot':
                return 'Reboot Sent';
            case 'push-updates':
                return 'Push Updates Sent';
            default:
                return '';
        }
    });

    /**
     * Message shown in the success modal after a license action completes.
     */
    public actionSuccessMessage = computed<string>(() => {
        const name = this.licenseDisplayName() || 'this license';
        switch (this.completedAction()) {
            case 'reboot':
                return `Reboot signal sent to "${name}". Devices will restart shortly.`;
            case 'push-updates':
                return `Latest updates pushed to "${name}". Devices may restart during the update.`;
            default:
                return '';
        }
    });

    /**
     * Confirm-button label for the action confirmation modal.
     */
    public actionModalConfirmText = computed<string>(() => {
        switch (this.pendingAction()) {
            case 'reboot':
                return 'Reboot';
            case 'push-updates':
                return 'Push Updates';
            default:
                return 'Confirm';
        }
    });

    /**
     * Handles action item click events from the popover.
     * Maps the popover label to a pending action and opens the confirmation modal.
     *
     * @param action - The label of the clicked action.
     */
    public onActionClick(action: string): void {
        switch (action) {
            case 'Reboot Devices':
                this.pendingAction.set('reboot');
                break;
            case 'Push Updates':
                this.pendingAction.set('push-updates');
                break;
            default:
                Logger.warn('SingleLicenseComponent', 'Unknown action:', action);
        }
    }

    /**
     * Closes the action confirmation modal without executing.
     */
    public closeActionModal(): void {
        this.pendingAction.set(null);
    }

    /**
     * Confirms and executes the pending license action.
     */
    public onConfirmAction(): void {
        const action = this.pendingAction();
        const id = this.licenseId();
        if (!action || !id) return;

        switch (action) {
            case 'reboot':
                this.socketService.sendRaspiReboot(id);
                break;
            case 'push-updates':
                this.socketService.sendRaspiContentsUpdate(id);
                break;
        }

        this.pendingAction.set(null);
        this.completedAction.set(action);
    }

    /**
     * Closes the post-action success modal.
     */
    public closeActionSuccessModal(): void {
        this.completedAction.set(null);
    }

    /**
     * Copies the provided text to the clipboard.
     */
    public async onCopyText(value: string): Promise<void> {
        if (!value) return;

        try {
            await navigator.clipboard.writeText(value);
        } catch {
            const textarea = document.createElement('textarea');
            textarea.value = value;
            textarea.style.position = 'fixed';
            textarea.style.left = '-9999px';
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
        }
    }

    /**
     * Active tab index
     */
    public activeTab = signal(0);

    /**
     * Controls analytics visibility in Details and Concept tab
     */
    public showAnalytics = signal(false);

    /**
     * Tabs configuration for the single license page
     */
    public tabs = [{ label: 'Details and Concept' }];

    /**
     * Toggles the analytics section visibility
     */
    public toggleAnalytics(): void {
        this.showAnalytics.update((val) => !val);
    }

    public licenseData = signal<{ label: string; value: string }[]>([]);

    /**
     * Identifier of the license currently shown.
     */
    public licenseId = signal<string>('');

    /**
     * Text the user must type to confirm deletion (license alias or key).
     */
    public licenseDeleteKey = signal<string>('');

    /**
     * Controls visibility of the delete-license confirmation modal.
     */
    public showDeleteLicenseModal = signal<boolean>(false);

    /**
     * Controls visibility of the post-deletion success modal.
     */
    public showDeleteSuccessModal = signal<boolean>(false);

    /**
     * Tracks the in-flight delete request for the modal spinner.
     */
    public isDeleting = signal<boolean>(false);

    // ── License tab ────────────────────────────────────────────────────────────

    /**
     * Signal to track selected rows in the license table.
     */
    public selectedRows = signal<any[]>([]);

    /**
     * Column definitions for the license table.
     */
    public licenseColumns = SINGLE_LICENSE_TABLE_COLUMNS;

    /**
     * Data shown in the license table.
     */
    public licenseTableData = signal<any[]>(SINGLE_LICENSE_TABLE_DATA);

    /**
     * Computed list of toolbar buttons, reacting to selection changes.
     */
    public toolbarButtons = computed<TableToolbarButton[]>(() => {
        const selectionCount = this.selectedRows().length;
        return [
            {
                buttonName: 'Bulk Delete',
                icon: 'delete',
                variant: selectionCount > 2 ? 'danger' : 'secondary',
            },
            {
                buttonName: 'Export',
                icon: 'export',
                variant: 'accent',
            },
        ];
    });

    /**
     * Dropdown config for timezone filter.
     */
    public timezoneConfig = signal({
        options: [
            { label: '(GMT-05:00) Eastern Time', value: 1 },
            { label: '(GMT-06:00) Central Time', value: 2 },
            { label: '(GMT-07:00) Mountain Time', value: 3 },
            { label: '(GMT-08:00) Pacific Time', value: 4 },
        ] as { label: string; value: number }[],
        placeholder: 'Timezone',
        fullWidth: false,
    });

    /**
     * Grouped filter options.
     */
    public filterOptions = signal<FilterOption[]>([
        {
            label: 'License Status',
            expanded: false,
            checked: false,
            children: [
                { label: 'Online', checked: false },
                { label: 'Offline', checked: false },
            ],
        },
        {
            label: 'Order by License Status',
            expanded: true,
            checked: true,
            children: [
                { label: 'Online', checked: false },
                { label: 'Offline', checked: false },
                { label: 'Display On', checked: false },
                { label: 'Display Off', checked: false },
            ],
        },
    ]);

    ngOnInit(): void {
        this.subNavbarService.setHideDate(true);

        const id = this.route.snapshot.params['id'];
        if (id) {
            this.licenseId.set(id);
            MainLayoutStore.setBreadcrumb('Fetching...');
            const traits = this.authService.userTraits() as {
                data?: { roleName?: string; userId?: string };
            } | null;
            const role = traits?.data?.roleName;
            const isDealer = role === 'Dealer' || role === 'Dealer Admin';

            this.licensesService.getLicenseById(id).subscribe({
                next: (license) => {
                    if (isDealer && license.ownerId !== traits?.data?.userId) {
                        this.router.navigate(['/dashboard']);
                        return;
                    }

                    const displayName = license.alias || license.licenseKey || id;
                    MainLayoutStore.setBreadcrumb(displayName);
                    this.licenseDisplayName.set(displayName);
                    this.licenseDeleteKey.set(license.licenseKey || id);

                    this.licenseData.set([
                        {
                            label: 'Dealer Name',
                            value: license.owner?.firstName + ' ' + license.owner?.lastName || '—',
                        },
                        { label: 'Business Hours', value: '24 Hours' },
                        { label: 'License Key', value: license.licenseKey || '—' },
                    ]);
                },
                error: () => {
                    if (isDealer) {
                        this.router.navigate(['/dashboard']);
                        return;
                    }
                    MainLayoutStore.setBreadcrumb(id);
                    this.licenseDisplayName.set(id);
                    this.licenseDeleteKey.set(id);
                },
            });
        }
    }

    ngOnDestroy(): void {
        MainLayoutStore.clearBreadcrumb();
        this.subNavbarService.setHideDate(false);
    }

    /**
     * Handles tab change events
     * @param index - The index of the selected tab
     */
    public onTabChange(index: number): void {
        this.activeTab.set(index);
    }

    /**
     * Handles selection change events from the license table.
     */
    public onLicenseSelectionChange(selectedRows: any[]): void {
        this.selectedRows.set(selectedRows);
    }

    /**
     * Opens the delete-license confirmation modal.
     */
    public openDeleteLicenseModal(): void {
        this.showDeleteLicenseModal.set(true);
    }

    /**
     * Closes the delete-license confirmation modal without deleting.
     */
    public closeDeleteLicenseModal(): void {
        this.showDeleteLicenseModal.set(false);
    }

    /**
     * Confirms and executes deletion of the current license.
     * Shows the success modal on completion and routes back to the licenses list.
     */
    public onConfirmDeleteLicense(): void {
        const id = this.licenseId();
        if (!id) return;

        this.licensesService
            .delete(id)
            .pipe(withLoading(this.isDeleting), takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => {
                    this.showDeleteLicenseModal.set(false);
                    this.showDeleteSuccessModal.set(true);
                },
                error: (err: unknown) => {
                    Logger.error('SingleLicenseComponent', 'Error deleting license:', err);
                },
            });
    }

    /**
     * Closes the post-deletion success modal and navigates back to the licenses list.
     */
    public onDeleteSuccessClose(): void {
        this.showDeleteSuccessModal.set(false);
        this.router.navigate(['/licenses']);
    }
}
